import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  KeyIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    activeLicenses: 0,
    totalProducts: 0,
    newUsersToday: 0,
    requestsToday: 0,
    pendingActivationKeys: 0,
    totalActivationKeys: 0,
    pendingClusterLicenses: 0,
    totalClusterLicenses: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = 'Admin Portal | Xloud Technologies';
  }, []);

  // Load dashboard data from API
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('[Admin Dashboard] No authentication token found');
          setLoading(false);
          return;
        }
        
        // Fetch stats and cluster licenses in parallel
        const [statsResponse, clusterLicensesResponse] = await Promise.allSettled([
          apiClient.get('/admin/stats'),
          apiClient.get('/cluster-licenses')
        ]);

        // apiClient already extracts the data, so use .value directly (not .value.data)
        const statsData = statsResponse.status === 'fulfilled' ? statsResponse.value : {};
        const clusterLicenses = clusterLicensesResponse.status === 'fulfilled' ? (clusterLicensesResponse.value || []) : [];
        
        setStats({
          totalUsers: statsData.totalUsers || 0,
          totalRequests: statsData.totalRequests || 0,
          pendingRequests: statsData.pendingRequests || 0,
          approvedRequests: statsData.approvedRequests || 0,
          activeLicenses: statsData.activeLicenses || 0,
          totalProducts: statsData.totalProducts || 0,
          newUsersToday: statsData.newUsersToday || 0,
          requestsToday: statsData.requestsToday || 0,
          pendingActivationKeys: statsData.pendingActivationKeys || 0,
          totalActivationKeys: statsData.totalActivationKeys || 0,
          pendingClusterLicenses: clusterLicenses.filter(c => c.status === 'PENDING').length,
          totalClusterLicenses: clusterLicenses.length
        });

        // Build recent activity from cluster licenses and other sources
        const activity = [];
        
        // Add recent cluster licenses
        clusterLicenses.slice(0, 3).forEach(cluster => {
          const isPending = cluster.status === 'PENDING';
          activity.push({
            id: `cluster-${cluster.id}`,
            message: isPending 
              ? `New cluster license request from ${cluster.user?.firstName} ${cluster.user?.lastName} - ${cluster.hostname}` 
              : `Cluster license ${cluster.status.toLowerCase()} - ${cluster.hostname}`,
            timestamp: new Date(cluster.createdAt).toLocaleString(),
            icon: isPending ? ClockIcon : CheckCircleIcon,
            color: isPending ? 'text-yellow-600' : 'text-green-600'
          });
        });

        setRecentActivity(activity);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data. Please try refreshing the page.');
        // Set default values on error
        setStats({
          totalUsers: 0,
          totalRequests: 0,
          pendingRequests: 0,
          approvedRequests: 0,
          activeLicenses: 0,
          totalProducts: 0,
          newUsersToday: 0,
          requestsToday: 0,
          pendingActivationKeys: 0,
          totalActivationKeys: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const statCards = [
    {
      name: 'Total Customers',
      value: stats.totalUsers,
      change: `+${stats.newUsersToday} today`,
      changeType: 'positive',
      icon: UsersIcon,
      color: 'bg-blue-500',
      href: '/dashboard/users'
    },
    {
      name: 'Activation Keys (XDeploy)',
      value: stats.totalActivationKeys,
      change: `${stats.pendingActivationKeys} pending configuration`,
      changeType: stats.pendingActivationKeys > 0 ? 'warning' : 'neutral',
      icon: KeyIcon,
      color: 'bg-purple-500',
      href: '/dashboard/admin/activation-keys'
    },
    {
      name: 'Cluster Licenses',
      value: stats.totalClusterLicenses,
      change: `${stats.pendingClusterLicenses} pending configuration`,
      changeType: stats.pendingClusterLicenses > 0 ? 'warning' : 'neutral',
      icon: CubeIcon,
      color: 'bg-green-500',
      href: '/dashboard/admin/cluster-licenses'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.firstName}! Here's what's happening with your licenses.</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            System Healthy
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            onClick={() => stat.href && (window.location.href = stat.href)}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                <p className={`text-xs mt-1 font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' :
                  stat.changeType === 'warning' ? 'text-amber-600' :
                  stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {stat.change}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-gray-600" />
                Recent Activity
              </h3>
            </div>
            <div className="p-6">
              {recentActivity.length === 0 ? (
                <div className="text-center py-12">
                  <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No recent activity</p>
                  <p className="text-xs text-gray-400 mt-1">Activity will appear here as users submit requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-150">
                      <div className={`p-2 rounded-lg bg-gray-100`}>
                        <activity.icon className={`h-4 w-4 ${activity.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => window.location.href = '/dashboard/users'}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors duration-150 group"
              >
                <div className="flex items-center">
                  <UsersIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <span className="text-sm font-medium text-gray-900">Manage Users</span>
                </div>
                <span className="text-gray-400">→</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard/admin/requests'}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors duration-150 group"
              >
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <span className="text-sm font-medium text-gray-900">Review Requests</span>
                  {stats.pendingRequests > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                      {stats.pendingRequests}
                    </span>
                  )}
                </div>
                <span className="text-gray-400">→</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard/admin/activation-keys'}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors duration-150 group"
              >
                <div className="flex items-center">
                  <KeyIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <span className="text-sm font-medium text-gray-900">Activation Keys (XDeploy)</span>
                  {stats.pendingActivationKeys > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                      {stats.pendingActivationKeys}
                    </span>
                  )}
                </div>
                <span className="text-gray-400">→</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard/admin/cluster-licenses'}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors duration-150 group"
              >
                <div className="flex items-center">
                  <CubeIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <span className="text-sm font-medium text-gray-900">Cluster Licenses</span>
                  {stats.pendingClusterLicenses > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                      {stats.pendingClusterLicenses}
                    </span>
                  )}
                </div>
                <span className="text-gray-400">→</span>
              </button>
            </div>
          </div>

          {/* System Status Card */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">License Server</span>
                <span className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Online
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Database</span>
                <span className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Connected
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Redis Cache</span>
                <span className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email Service</span>
                <span className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Ready
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;